export interface JobUpdateEvent {
  job: {
    id: string;
    name: string;
    queue: string;
  };
  status: "waiting" | "progress" | "completed" | "error";
  data?: any;
  progress?: {
    total: number;
    completed: number;
  };
}
export default class OnvoBase {
  #apiKey: string;
  _endpoint: string;

  // Base fetch method
  /**
   * Fetches data from the API.
   * @param url - The URL to fetch data from.
   * @param method - The HTTP method to use. Defaults to "GET".
   * @param body - The data to send with the request.
   * @param isForm - Indicates if the request is a form request.
   * @returns The response from the API.
   * @throws {Error} If there is an error making the request.
   */
  async _fetchBase(
    url: string,
    method?: "GET" | "PUT" | "POST" | "DELETE" | "PATCH",
    body?: any,
    isForm?: boolean
  ): Promise<any> {
    let headers: any = {
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
    };
    if (this.#apiKey && this.#apiKey.trim() !== "") {
      headers["x-api-key"] = this.#apiKey;
    }
    if (!isForm) {
      headers["Content-Type"] = "application/json";
    }
    const response = await fetch(this._endpoint + url, {
      method: method || "GET",
      headers: headers,
      body: isForm ? body : body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      let data: any = await response.json();
      throw new Error(data.message);
    } else {
      let data = await response.json();
      return data;
    }
  }

  async _fetchStreamBase(url: string, method?: "GET" | "PUT" | "POST" | "DELETE" | "PATCH", body?: any, onProgress?: (event: JobUpdateEvent) => void) {

    if (onProgress) {
      let headers: any = {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
        "x-stream": "true",
      };
      if (this.#apiKey && this.#apiKey.trim() !== "") {
        headers["x-api-key"] = this.#apiKey;
      }
      // Use streaming approach when callback is provided
      const response = await fetch(this._endpoint + url, {
        method: method || "POST",
        headers: headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok && !response.body) {
        const errorData = await response.json().catch(() => ({ message: "Unknown error" }));
        throw new Error(errorData.message || "Failed to update dashboard cache");
      }

      // Process the stream
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let finalResult: any = null;

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Process each line as a separate JSON object
          const text = decoder.decode(value);
          const lines = text.split('\n').filter(line => line.trim());

          for (const line of lines) {
            const event = JSON.parse(line) as JobUpdateEvent;
            if (event.status === "progress") {
              onProgress(event);
            }
            if (event.status === "error") {
              throw new Error(event.data.error);
            }
            // Store the final result for return value
            if (event.status === 'completed') {
              finalResult = event.data;
            }
          }
        }
      } catch (error) {
        console.error("Error running job:", error);
        throw error;
      } finally {
        reader.releaseLock();
      }

      return finalResult;
    } else {
      let headers: any = {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS"
      };

      if (this.#apiKey && this.#apiKey.trim() !== "") {
        headers["x-api-key"] = this.#apiKey;
      }
      // Use traditional approach when no callback is provided
      const response = await fetch(this._endpoint + url, {
        method: method || "POST",
        headers: headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message);
      }
      return data.data;
    }
  }

  /**
   * Fetches a Blob from the API.
   * @param url - The URL to fetch data from.
   * @returns The response from the API as a Blob.
   * @throws {Error} If there is an error making the request.
   */
  async _fetchBlob(url: string): Promise<Blob> {
    try {
      let headers: any = {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
      };
      if (this.#apiKey && this.#apiKey.trim() !== "") {
        headers["x-api-key"] = this.#apiKey;
      }
      const response = await fetch(this._endpoint + url, {
        method: "GET",
        headers: headers,
      }).then((response) => response.blob());
      return response;
    } catch (error: any) {
      throw new Error("Error in making the request: " + error.message);
    }
  }

  constructor(apiKey: string, options?: { endpoint: string }) {
    if (apiKey === undefined || apiKey === null)
      throw new Error("API key is required");
    this.#apiKey = apiKey;
    this._endpoint = options?.endpoint || "https://dashboard.onvo.ai";
  }
}
